/**
 * SignUp — new user registration screen (step 1 of 2).
 *
 * Calls Supabase /auth/v1/signup directly, then stores the new session and
 * basic user info in sessionStorage before navigating to OrganizationDetails
 * (step 2) where the tenant record is created.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import './Signup.css';
import logo from '../../components/images/logo.png';
import characterImage from '../../components/images/onboarding_image.png';

const SignUp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const { firstName, lastName, email, password, confirmPassword } = formData;

    // Client-side validation before hitting the API
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!agreedToTerms) {
      setError('Please agree to the Privacy Policy and Terms of Service');
      return;
    }

    setIsLoading(true);

    try {
      /*
       * Direct REST call bypasses the Supabase JS client to avoid
       * intermittent hangs during PKCE initialisation on first load.
       */
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/auth/v1/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`
        },
        body: JSON.stringify({
          email: email.trim(),
          password,
          data: {
            first_name: firstName.trim(),
            last_name: lastName.trim()
          }
        })
      });

      const responseData = await response.json();

      if (!response.ok) {
        // Duplicate email — redirect to login
        if (responseData.msg?.includes('already registered') ||
            responseData.message?.includes('already registered') ||
            responseData.error_description?.includes('already registered') ||
            response.status === 422) {
          setError('This email is already registered. Redirecting to login...');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }
        setError(responseData.message || responseData.msg || 'Signup failed');
        setIsLoading(false);
        return;
      }

      if (!responseData.user) {
        setError('Signup failed. Please try again.');
        setIsLoading(false);
        return;
      }

      // Persist user info for the next step (OrganizationDetails)
      sessionStorage.setItem('signupData', JSON.stringify({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        userId: responseData.user.id
      }));

      // Persist session so AuthContext picks it up after navigation
      sessionStorage.setItem('supabase-auth-token', JSON.stringify({
        access_token: responseData.access_token,
        refresh_token: responseData.refresh_token,
        user: responseData.user,
        expires_at: responseData.expires_at
      }));

      // Sync the Supabase client so DB calls use the user JWT (not the anon key)
      supabase.auth.setSession({
        access_token: responseData.access_token,
        refresh_token: responseData.refresh_token
      }).catch(() => {});

      setIsLoading(false);
      setTimeout(() => navigate('/organization-details', { replace: true }), 100);

    } catch (err) {
      console.error('Signup error:', err.message);
      if (err.message?.includes('Failed to fetch') || err.name === 'TypeError') {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(`Signup failed: ${err.message}`);
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-left">
        <div className="signup-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img src={logo} alt="TalentScope360" />
        </div>

        <div className="signup-content">
          <h1 className="signup-title">Sign up</h1>
          <p className="signup-subtitle">Please enter your details..</p>

          <form className="signup-form" onSubmit={handleSubmit}>
            {error && (
              <div className="signup-error" style={{ 
                color: '#dc2626', 
                backgroundColor: '#fee2e2', 
                padding: '12px', 
                borderRadius: '6px', 
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            <input
              type="text"
              name="firstName"
              placeholder="First Name"
              className="signup-input"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            />

            <input
              type="text"
              name="lastName"
              placeholder="Last Name"
              className="signup-input"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            />

            <input
              type="email"
              name="email"
              placeholder="Email"
              className="signup-input"
              value={formData.email}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            />

            <div className="signup-password-field">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Create Password"
                className="signup-input"
                value={formData.password}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                minLength={6}
              />
              <button
                type="button"
                className="signup-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                aria-label="Toggle password visibility"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {showPassword ? (
                    <>
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </>
                  ) : (
                    <>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </>
                  )}
                </svg>
              </button>
            </div>

            <div className="signup-password-field">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                placeholder="Confirm Password"
                className="signup-input"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                minLength={6}
              />
              <button
                type="button"
                className="signup-password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isLoading}
                aria-label="Toggle confirm password visibility"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {showConfirmPassword ? (
                    <>
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </>
                  ) : (
                    <>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </>
                  )}
                </svg>
              </button>
            </div>

            <div className="signup-terms">
              <label className="signup-checkbox-label">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  disabled={isLoading}
                />
                <span>I agree to the <a href="/privacy-policy">Privacy Policy</a> and <a href="/terms">Terms of Service</a></span>
              </label>
            </div>

            <button type="submit" className="signup-button" disabled={isLoading || !agreedToTerms}>
              {isLoading ? 'Creating Account...' : 'Get Started'}
            </button>
          </form>

          <div className="signup-footer">
            <p>Already have an account? <a href="/login">Login</a></p>
            <p>Want to explore first? <a href="/demo">View demo</a></p>
          </div>
        </div>
      </div>

      <div className="signup-right">
        <div className="signup-right-content">
          <div className="signup-character">
            <img src={characterImage} alt="Character" />
          </div>
          <h2 className="signup-right-title">Streamline your Talent Management.</h2>
          <p className="signup-right-text">
            Transform your workforce into a competitive advantage with TalentScope360.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;